import { useMemo } from 'react';

import { window } from '../../../../globals';
import useReportOptions from '../../../../hooks/api/reports/useReportOptions';
import { selectOptionFactory } from '../../../../utils';
import { useProjectSettingsContext } from '../context';
import { useCanUserEditProject } from '../permissions';
import type { SelectOption } from '../../../../types';

import type { ProjectReportsSettings } from './types';

type ResolvedSettings = {
  defaultSavedOptionLicensingReport: SelectOption<number> | null;
  defaultSavedOptionSbom: SelectOption<number> | null;
};

export type UseProjectReportsSettingsResult = {
  resolvedSettings: ResolvedSettings;
  reportOptionSelectOptions: Array<SelectOption<number>>;
  isLoading: boolean;
  isSaving: boolean;
  isDirty: boolean;
  isSaveButtonDisabled: boolean;
  canEdit: boolean;
  onUpdate: (update: Partial<ProjectReportsSettings>) => void;
  onSave: () => Promise<unknown>;
};

export const useProjectReportsSettings = (): UseProjectReportsSettingsResult => {
  const { reports } = useProjectSettingsContext();
  const canEdit = useCanUserEditProject(reports.locator);

  const organizationId = window.user?.organizationId ?? 0;
  const { data: reportOptionsData, isLoading } = useReportOptions(organizationId);

  const reportOptionSelectOptions = useMemo(
    () => reportOptionsData?.results.map((option) => selectOptionFactory(option.id, option.name)) ?? [],
    [reportOptionsData],
  );

  return {
    resolvedSettings: useMemo(
      () => ({
        defaultSavedOptionLicensingReport: reports.settings.defaultSavedOptionLicensingReport
          ? (reportOptionSelectOptions.find(
              (opt) => opt.value === reports.settings.defaultSavedOptionLicensingReport,
            ) ?? null)
          : null,
        defaultSavedOptionSbom: reports.settings.defaultSavedOptionSbom
          ? (reportOptionSelectOptions.find((opt) => opt.value === reports.settings.defaultSavedOptionSbom) ?? null)
          : null,
      }),
      [reports.settings, reportOptionSelectOptions],
    ),
    reportOptionSelectOptions,
    isLoading,
    isSaving: reports.isSaving,
    isDirty: reports.isDirty,
    isSaveButtonDisabled: !canEdit || !reports.isDirty || reports.isSaving || isLoading,
    canEdit,
    onUpdate: reports.onUpdate,
    onSave: reports.onSave,
  };
};
